import React, { Component } from 'react';
import { TooltipTrigger, ActionButton, Icon } from 'components';
import PropTypes from 'prop-types';
import cx from 'classnames';
import moment from 'moment';
import './style.scss';

const monthShortNames = moment.monthsShort();
const monthFullNames = moment.months();

class MonthPickerV2 extends Component {
  static propTypes = {
    onChange: PropTypes.func,
    defaultValue: PropTypes.any,
    range: PropTypes.array,
    displayDateRange: PropTypes.bool,
    disableFuture: PropTypes.bool,
    disableCurrentMonth: PropTypes.bool,
    disablePast: PropTypes.bool,
    clearable: PropTypes.bool,
    'data-cy': PropTypes.string
  };

  static defaultProps = {
    defaultValue: null,
    range: null,
    displayDateRange: false,
    disableFuture: false,
    disableCurrentMonth: false,
    disablePast: false,
    clearable: false
  };

  tooltip = null;

  constructor(props) {
    super(props);
    let date;
    if (Array.isArray(props.range) && props.range.length === 2) {
      date = props.range[1];
    } else {
      date = Array.isArray(props.defaultValue) ? props.defaultValue[0] : props.defaultValue;
    }
    this.state = {
      month: date != null ? moment(date).month() : null,
      year: date != null ? moment(date).year() : moment().year()
    };
  }

  @bind
  changeMonth(month, isDisabled) {
    if (isDisabled) return;
    this.setState({ month }, () => this.handleChange());
    this.tooltip.toggleTooltip(false);
  }

  @bind
  setPrevYear() {
    const { month, year } = this.state;
    let prevMonth = month;
    let prevYear = year - 1;

    if (this.isMonthDisabled(prevYear, month)) {
      prevMonth = 11;
    }

    // if semi-annual range is active, DO NOT recalc range on year nav
    if (Array.isArray(this.props.range)) {
      this.setState({ year: prevYear, month: prevMonth });
    } else {
      this.setState({ year: prevYear, month: prevMonth }, () => this.handleChange());
    }
  }

  @bind
  setNextYear() {
    const { month, year } = this.state;
    let nextMonth = month;
    let nextYear = year + 1;

    if (this.isMonthDisabled(nextYear, month)) {
      nextMonth = 0;
    }

    // if semi-annual range is active, DO NOT recalc range on year nav
    if (Array.isArray(this.props.range)) {
      this.setState({ year: nextYear, month: nextMonth });
    } else {
      this.setState({ year: nextYear, month: nextMonth }, () => this.handleChange());
    }
  }

  @bind
  setValue(month, year) {
    this.setState({ month, year }, () => this.handleChange());
  }

  @bind
  isMonthDisabled(currentYear, currentMonth) {
    const { disableFuture, disableCurrentMonth, disablePast } = this.props;
    const checked = moment({ year: currentYear, month: currentMonth });
    const nowMonth = moment().startOf('month');
    return (
      (disableFuture && checked.isAfter(nowMonth)) ||
      (disableCurrentMonth && checked.isSame(nowMonth)) ||
      (disablePast && checked.isBefore(nowMonth))
    );
  }

  handleChange() {
    const { month, year } = this.state;
    const { onChange, range } = this.props;
    if (month == null) return onChange(undefined);

    if (Array.isArray(range) && range.length === 2) {
      const start = moment([year, month, 1])
        .subtract(5, 'months')
        .startOf('month')
        .toDate();
      const end = moment([year, month, 1])
        .endOf('month')
        .toDate();
      onChange([start, end]);
    } else {
      const start = moment([year, month, 1])
        .startOf('month')
        .toDate();
      const end = moment([year, month, 1])
        .endOf('month')
        .toDate();
      onChange([start, end]);
    }
  }

  @bind
  onClear(e) {
    e.stopPropagation();
    this.setValue(null, this.state.year);
  }

  @bind
  isNextYearButtonDisabled() {
    if (!this.props.disableFuture) return false;
    const currentYear = moment().year();
    return (
      this.state.year === currentYear ||
      (this.state.year === currentYear - 1 && this.props.disableCurrentMonth && moment().month() === 0)
    );
  }

  @bind
  isPastYearButtonDisabled() {
    if (!this.props.disablePast) return false;
    const currentYear = moment().year();
    return (
      this.state.year === currentYear ||
      (this.state.year === currentYear + 1 && this.props.disableCurrentMonth && moment().month() === 11)
    );
  }

  renderRow(months, offset) {
    const { range } = this.props;
    const { year, month: current } = this.state;
    let highlightRange = null;

    if (Array.isArray(range) && range.length === 2) {
      const [start, end] = range;
      highlightRange = {
        start: moment(start),
        end: moment(end)
      };
    }

    return months.map((m, idx) => {
      const idxMonth = offset + idx;
      const disabled = this.isMonthDisabled(year, idxMonth);
      const isActive = highlightRange
        ? moment({ year, month: idxMonth }).isBetween(highlightRange.start, highlightRange.end, 'month', '[]')
        : current === idxMonth;

      const cellCN = cx('month-picker__cell', {
        'month-picker__cell--active': isActive,
        'month-picker__cell--disabled': disabled
      });

      return (
        <div key={m} className={cellCN} onClick={() => this.changeMonth(idxMonth, disabled)}>
          {m}
        </div>
      );
    });
  }

  @bind
  renderContent() {
    const { 'data-cy': dataCY } = this.props;
    const rows = [];
    for (let i = 0; i < monthShortNames.length; i += 3) {
      rows.push(
        <div className="month-picker__row" data-cy={`${dataCY}-${i}`} key={i}>
          {this.renderRow(monthShortNames.slice(i, i + 3), i)}
        </div>
      );
    }
    return (
      <div className="month-picker__content">
        <div className="month-picker__year">
          <ActionButton
            iconName="chevron-down"
            className="month-picker__year-arrow-left"
            size={12}
            onClick={this.setPrevYear}
            disabled={this.isPastYearButtonDisabled()}
            data-cy={`${dataCY}-prev`}
          />
          <div className="month-picker__year-value">{this.state.year}</div>
          <ActionButton
            iconName="chevron-down"
            className="month-picker__year-arrow-right"
            size={12}
            onClick={this.setNextYear}
            disabled={this.isNextYearButtonDisabled()}
            data-cy={`${dataCY}-next`}
          />
        </div>
        <div className="month-picker__months">{rows}</div>
      </div>
    );
  }

  render() {
    const { month, year } = this.state;
    const { disabled, clearable, displayDateRange, range, 'data-cy': dataCY } = this.props;
    const triggerCN = cx('month-picker__trigger', {
      'month-picker__trigger--placeholder': month == null,
      semiannual: Array.isArray(range) && range.length === 2
    });

    let label;
    if (Array.isArray(range) && range.length === 2) {
      const [s, e] = range;
      label = `${moment(s).format('MMM YYYY')} – ${moment(e).format('MMM YYYY')}`;
    } else if (month != null) {
      label = displayDateRange
        ? `${moment([year, month, 1]).format('MMM D')} – ${moment([year, month, 1])
            .endOf('month')
            .format('MMM D')}`
        : `${monthFullNames[month]}, ${year}`;
    } else {
      label = 'Select Month';
    }

    return (
      <TooltipTrigger
        noArrow
        white
        className="month-picker"
        triggerOn="click"
        renderTooltip={this.renderContent}
        ref={node => (this.tooltip = node)}
        disabled={disabled}
        keepOpenDisabled
        data-cy={dataCY}
      >
        <div className={triggerCN}>
          <div className="month-picker__label">{label}</div>
          {clearable && month != null ? (
            <ActionButton iconName="close-circle" size={14} className="month-picker__icon" onClick={this.onClear} />
          ) : (
            <Icon name="calendar" className="month-picker__icon" size={18} />
          )}
        </div>
      </TooltipTrigger>
    );
  }
}

export default MonthPickerV2;
